import { useCallback, useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useSettings } from "../context/SettingsContext";
import {
  Pill, Sparkles, Plus, Trash2, Clock, Check, Edit3, X, ListChecks, CheckCircle2,
  Bell, BellOff, AlertTriangle, Info,
} from "lucide-react";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";
const TEAL_SOFT = "#F0FDFA";

/** Convert "HH:MM" (24h) → "8:00 AM" for display. */
function fmtTime(hhmm) {
  if (!hhmm) return "Anytime";
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  const ap = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${hh}:${mm} ${ap}`;
}

export default function Supplements() {
  const { settings } = useSettings();
  // Master AI switch — when off, hide the AI Stack Builder card entirely.
  const aiEnabled = (settings?.feature_flags?.ai_features ?? true) !== false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parseText, setParseText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualTime, setManualTime] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editTime, setEditTime] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/supplements");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("Supplements: failed to load", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doneCount = items.filter((s) => s.completed_today).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const runParse = async () => {
    if (!parseText.trim()) return;
    setParsing(true);
    try {
      const { data } = await api.post("/supplements/parse", { text: parseText });
      setPreviewRows(Array.isArray(data.items) ? data.items : []);
      if (!data.items?.length) toast.error("Couldn't find any supplements in that text. Try being more specific.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setParsing(false);
    }
  };

  const addAll = async () => {
    if (!previewRows?.length) return;
    setAdding(true);
    try {
      const { data } = await api.post("/supplements/bulk", { items: previewRows });
      toast.success(`Added ${data.created} supplement${data.created === 1 ? "" : "s"}.`);
      setPreviewRows(null);
      setParseText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!manualName.trim()) return;
    try {
      await api.post("/supplements", { name: manualName.trim(), time_hhmm: manualTime.trim() || null });
      setManualName("");
      setManualTime("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleDone = async (supp) => {
    // Optimistic UI
    setItems((prev) => prev.map((s) => (s.id === supp.id ? { ...s, completed_today: !s.completed_today } : s)));
    try {
      await api.post(`/supplements/${supp.id}/toggle`);
    } catch {
      // Revert on failure
      setItems((prev) => prev.map((s) => (s.id === supp.id ? { ...s, completed_today: supp.completed_today } : s)));
      toast.error("Couldn't update. Try again.");
    }
  };

  const startEdit = (supp) => {
    setEditingId(supp.id);
    setEditName(supp.name);
    setEditTime(supp.time_hhmm || "");
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await api.patch(`/supplements/${editingId}`, {
        name: editName.trim(),
        time_hhmm: editTime.trim() || null,
      });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (supp) => {
    // Snapshot before delete for the Undo toast.
    const snapshot = { name: supp.name, time_hhmm: supp.time_hhmm || null, note: supp.note || null };
    try {
      await api.delete(`/supplements/${supp.id}`);
      setItems((prev) => prev.filter((s) => s.id !== supp.id));
      toast(`Removed: ${snapshot.name}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/supplements", snapshot);
              await load();
              toast.success("Restored");
            } catch (err) { toast.error(formatApiError(err)); }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // -------- Reminder notifications --------
  const [notifPrefs, setNotifPrefs] = useState({ browser: false, email: false });
  const [firedToday, setFiredToday] = useState({});   // { "id-HHMM": true }

  useEffect(() => {
    api.get("/supplement-notify-prefs").then(({ data }) => setNotifPrefs(data)).catch(() => {});
  }, []);

  const toggleBrowserNotif = async () => {
    if (!notifPrefs.browser) {
      if (typeof Notification === "undefined") {
        toast.error("This browser doesn't support notifications.");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Enable notifications in your browser settings first.");
        return;
      }
    }
    const next = { ...notifPrefs, browser: !notifPrefs.browser };
    const { data } = await api.patch("/supplement-notify-prefs", { browser: next.browser });
    setNotifPrefs(data);
    toast.success(data.browser ? "Reminders on — you'll get a nudge at each supplement's time." : "Reminders off.");
  };

  const toggleEmailNotif = async () => {
    const next = { ...notifPrefs, email: !notifPrefs.email };
    const { data } = await api.patch("/supplement-notify-prefs", { email: next.email });
    setNotifPrefs(data);
    toast.success(data.email ? "Email nudges on." : "Email nudges off.");
  };

  // Polling loop: every 30s check if any supplement's `time_hhmm` matches
  // the current wall clock (±30s) and fire a browser Notification once per day.
  useEffect(() => {
    if (!notifPrefs.browser || typeof Notification === "undefined" || Notification.permission !== "granted") return undefined;
    const check = () => {
      const now = new Date();
      const hhmmNow = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const todayKey = now.toISOString().slice(0, 10);
      for (const s of items) {
        if (!s.time_hhmm || s.completed_today) continue;
        if (s.time_hhmm !== hhmmNow) continue;
        const guard = `${todayKey}|${s.id}|${s.time_hhmm}`;
        if (firedToday[guard]) continue;
        try {
          // eslint-disable-next-line no-new
          new Notification("Time for " + s.name, {
            body: `Scheduled at ${fmtTime(s.time_hhmm)} — open The Amino Exchange to check it off.`,
            icon: "/icon-192.png",
            tag: guard,
          });
          setFiredToday((prev) => ({ ...prev, [guard]: true }));
        } catch (err) { console.warn("Supplement notification blocked:", err); }
      }
    };
    check();
    const t = setInterval(check, 30_000);
    return () => clearInterval(t);
  }, [items, notifPrefs.browser, firedToday]);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Daily Stack"
        title="Supplements"
        icon={<Pill className="w-10 h-10 text-[#0D9488]" />}
        subtitle="Type your stack in plain English and let the AI build your daily checklist. Resets automatically at midnight."
      />

      {/* Progress bar */}
      {items.length > 0 && (
        <div
          className="bg-white border border-slate-200 rounded-xl p-5 my-6 flex items-center gap-5 flex-wrap"
          data-testid="supp-progress"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-[#0D9488]" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#0D9488]">Today's stack</span>
              </div>
              <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="supp-progress-text">
                {doneCount} / {items.length} · {pct}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${TEAL}, ${TEAL_D})` }}
                data-testid="supp-progress-bar"
              />
            </div>
          </div>
          {pct === 100 && (
            <div className="flex items-center gap-2 text-[#0D9488] font-semibold text-sm">
              <CheckCircle2 className="w-5 h-5" /> Stack complete for today
            </div>
          )}
        </div>
      )}

      {/* Reminder notifications */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 flex items-center justify-between flex-wrap gap-4" data-testid="supp-notify-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: TEAL_SOFT, color: TEAL }}>
            {notifPrefs.browser || notifPrefs.email ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>Reminders</div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5">Get nudged at each supplement's time</div>
            <p className="text-xs text-slate-500 mt-1 max-w-md leading-relaxed">
              Browser notifications fire on this device only. Email nudges are delivered via Resend at each scheduled time.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleBrowserNotif}
            className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${
              notifPrefs.browser
                ? "bg-teal-50 border-teal-300 text-teal-700"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
            data-testid="supp-notify-browser"
          >
            <Bell className="w-3.5 h-3.5" />
            Browser {notifPrefs.browser ? "on" : "off"}
          </button>
          <button
            type="button"
            onClick={toggleEmailNotif}
            className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${
              notifPrefs.email
                ? "bg-teal-50 border-teal-300 text-teal-700"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
            data-testid="supp-notify-email"
          >
            Email {notifPrefs.email ? "on" : "off"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {aiEnabled && (
        /* AI parse card */
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="supp-ai-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#0D9488]" />
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">AI Stack Builder</div>
          </div>
          <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Describe your stack</h3>
          <p className="text-sm text-slate-500 mb-4">
            Free-form text — the AI extracts each supplement and its time.
          </p>
          <Textarea
            rows={4}
            value={parseText}
            onChange={(e) => setParseText(e.target.value)}
            placeholder="e.g. I take a multivitamin at 8 AM, magnesium glycinate at 9 PM, and vitamin D3 with breakfast."
            className="bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
            data-testid="supp-ai-input"
          />
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <p className="text-[11px] text-slate-400">Powered by Claude Sonnet 4.6 · handles ambiguous times.</p>
            <Button
              type="button"
              onClick={runParse}
              disabled={!parseText.trim() || parsing}
              className="text-white"
              style={{ background: parsing ? TEAL_D : TEAL }}
              data-testid="supp-ai-parse"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> {parsing ? "Parsing…" : "Parse stack"}
            </Button>
          </div>

          {previewRows !== null && (
            <div className="mt-5 border border-teal-200 bg-teal-50/40 rounded-lg p-4" data-testid="supp-ai-preview">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#0D9488] mb-2">
                Preview · {previewRows.length} row{previewRows.length === 1 ? "" : "s"}
              </div>
              {previewRows.length === 0 ? (
                <div className="text-sm text-slate-500">No supplements detected — refine your text.</div>
              ) : (
                <div className="space-y-2">
                  {previewRows.map((r, i) => (
                    <div
                      key={`${r.name}-${r.dose_amount || ""}-${i}`}
                      className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5 text-sm"
                      data-testid={`supp-ai-preview-${i}`}
                    >
                      <span className="font-medium text-slate-900">{r.name}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtTime(r.time_hhmm)}
                      </span>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      onClick={addAll}
                      disabled={adding}
                      className="text-white"
                      style={{ background: adding ? TEAL_D : TEAL }}
                      data-testid="supp-ai-add-all"
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> {adding ? "Adding…" : `Add ${previewRows.length}`}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPreviewRows(null)}
                      className="border-slate-200 text-slate-700 hover:bg-slate-100"
                      data-testid="supp-ai-discard"
                    >
                      Discard
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Manual add */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="supp-manual-card">
          <div className="flex items-center gap-2 mb-1">
            <Plus className="w-4 h-4 text-[#0D9488]" />
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Add manually</div>
          </div>
          <h3 className="font-display font-bold text-xl text-slate-900 mb-1">One at a time</h3>
          <p className="text-sm text-slate-500 mb-4">
            Prefer to add supplements individually? Use the form below.
          </p>
          <form onSubmit={addManual} className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Name</span>
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Vitamin D3 5000IU"
                className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                data-testid="supp-manual-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Time (optional)</span>
              <Input
                type="time"
                value={manualTime}
                onChange={(e) => setManualTime(e.target.value)}
                className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                data-testid="supp-manual-time"
              />
            </label>
            <Button
              type="submit"
              disabled={!manualName.trim()}
              className="text-white w-full sm:w-auto"
              style={{ background: manualName.trim() ? TEAL : "#94A3B8" }}
              data-testid="supp-manual-submit"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add supplement
            </Button>
          </form>
        </div>
      </div>

      {/* Today's checklist */}
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="supp-checklist-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488] mb-0.5">Checklist</div>
            <h3 className="font-display font-bold text-xl text-slate-900">Today's supplements</h3>
          </div>
          <span className="text-[11px] text-slate-400">Resets at midnight in the site timezone.</span>
        </div>

        {loading ? (
          <div className="h-24 rounded-lg bg-slate-100 animate-pulse" />
        ) : items.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center bg-slate-50/40" data-testid="supp-empty">
            <Pill className="w-6 h-6 mx-auto mb-2 text-slate-400" />
            <div className="text-sm font-semibold text-slate-800 mb-1">Your stack is empty</div>
            <div className="text-xs text-slate-500 max-w-sm mx-auto">
              Describe your stack above and let the AI build your daily checklist — or add one manually.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((s) => {
              const isEditing = editingId === s.id;
              return (
                <div
                  key={s.id}
                  className={`border rounded-lg p-3 flex items-center gap-3 transition-colors ${
                    s.completed_today ? "border-teal-200 bg-teal-50/50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`supp-row-${s.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleDone(s)}
                    aria-label={s.completed_today ? "Mark as not taken" : "Mark as taken"}
                    className="w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all shrink-0"
                    style={{
                      background: s.completed_today ? TEAL : "#FFFFFF",
                      borderColor: s.completed_today ? TEAL : "#CBD5E1",
                    }}
                    data-testid={`supp-check-${s.id}`}
                  >
                    {s.completed_today && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                  </button>

                  {isEditing ? (
                    <div className="flex-1 flex flex-col sm:flex-row gap-2 items-stretch">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 h-9 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                      />
                      <Input
                        type="time"
                        value={editTime}
                        onChange={(e) => setEditTime(e.target.value)}
                        className="h-9 w-28 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                      />
                      <Button type="button" size="sm" onClick={saveEdit} className="h-9 text-white" style={{ background: TEAL }} data-testid={`supp-save-${s.id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={cancelEdit} className="h-9 border-slate-200 text-slate-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm ${s.completed_today ? "text-slate-500 line-through decoration-1" : "text-slate-900"}`}>
                          {s.name}
                        </div>
                        {s.time_hhmm && (
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" /> {fmtTime(s.time_hhmm)}
                          </div>
                        )}
                        {Array.isArray(s.interactions) && s.interactions.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid={`supp-interactions-${s.id}`}>
                            {s.interactions.map((it, i) => {
                              const warn = it.level === "warn";
                              const Icon = warn ? AlertTriangle : Info;
                              return (
                                <div
                                  key={`${s.id}-${it.level || ""}-${(it.text || "").slice(0, 24)}-${i}`}
                                  className={`flex items-start gap-1.5 text-[11px] rounded-md px-2 py-1 border ${
                                    warn
                                      ? "bg-amber-50 border-amber-200 text-amber-800"
                                      : "bg-slate-50 border-slate-200 text-slate-600"
                                  }`}
                                >
                                  <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${warn ? "text-amber-600" : "text-slate-400"}`} />
                                  <span className="leading-snug">{it.message}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => startEdit(s)}
                        className="w-8 h-8 rounded-md border border-slate-200 hover:border-slate-400 flex items-center justify-center text-slate-500 shrink-0"
                        title="Edit"
                        data-testid={`supp-edit-${s.id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(s)}
                        className="w-8 h-8 rounded-md border border-rose-200 hover:bg-rose-50 flex items-center justify-center text-rose-600 shrink-0"
                        title="Delete"
                        data-testid={`supp-delete-${s.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
